import React from "react"
import * as layouts from "layouts"
import * as api from "brandguard/api"
import * as bglayouts from "brandguard/components/layouts"
import classnames from "classnames"
import * as scores from "brandguard/components/layouts/scores"
import { useSettings } from "accounts/settings"
import { State } from "accounts/api"
import {
	LookAndFeel,
	Violence,
	Nudity,
	Explanation,
	Racism,
	FontDetection,
	BackgroundColor,
	ReferenceChecks,
	Grammar,
	ArtifactDetection,
	ProductDetection,
} from "brandguard/display/scores"
import LogoDetection from "brandguard/display/scores/logo_detection"

interface props extends layouts.containers.ContainerProps {
	item: api.DocumentSearchResponseItem
	currentPage?: api.DocumentPage
	prediction(evt: api.DocumentSearchResponseItem): api.Prediction
}

export default function Scoring(props: props): JSX.Element {
	const { item, currentPage, prediction, className, mx, ...rest } = props
	const parentRest = { mx }
	const settings = useSettings()

	// Use current page scores if available, otherwise use document-level scores
	const getScore = (pageScore?: number, eventScore?: number): number => {
		if (currentPage && pageScore !== undefined && pageScore !== -1) return pageScore
		if (eventScore !== undefined && eventScore !== -1) return eventScore
		return -1
	}

	// Convert DocumentPage to api.Media format for explanation components
	const pageAsMedia = currentPage ? ({ id: currentPage.id } as api.Media) : undefined

	const onBrand = {
		title: "Brand Principles",
		indev: false,
		subtitle: "Detailed view of the Brand Principles",
		subscores: [
			{
				title: "Look & Feel",
				score: getScore(currentPage?.doc_lookandfeel, item.event?.doc_lookandfeel),
				inverse: false,
				indev: false,
				available: settings.doc_lookandfeel,
				additions: pageAsMedia ? <LookAndFeel event={pageAsMedia} /> : undefined,
			},
			{
				title: "Brand Consistency",
				...scores.assessed(item.event?.artifact_score, item.event?.artifact_match_description ?? ""),
				inverse: false,
				indev: false,
				additions: <ArtifactDetection event={item.event!} />,
			},
		] as scores.subScoreStruct[],
	} as scores.scoreStruct

	const onStyle = {
		title: "Precision Elements",
		indev: false,
		subtitle: "Detailed view of the Precision Elements",
		subscores: [
			{
				title: "Correct Logo(s)",
				score: getScore(currentPage?.doc_logo_detection, item.event?.doc_logo_detection),
				inverse: false,
				indev: false,
				available: settings.doc_logo_detection,
				additions: pageAsMedia ? <LogoDetection event={pageAsMedia} /> : undefined,
			},
			{
				title: "Correct Font(s)",
				score: getScore(currentPage?.doc_font_detection, item.event?.doc_font_detection),
				inverse: false,
				indev: false,
				available: settings.doc_font_detection,
				additions: pageAsMedia ? <FontDetection event={pageAsMedia} /> : undefined,
			},
			{
				title: "Colors",
				score: getScore(currentPage?.doc_background_colors, item.event?.doc_background_colors),
				inverse: false,
				indev: false,
				available: settings.doc_background_colors,
				additions: pageAsMedia ? <BackgroundColor event={pageAsMedia} /> : undefined,
			},
			{
				title: "Custom Rules",
				score: getScore(currentPage?.doc_rules, item.event?.doc_rules),
				inverse: false,
				indev: false,
				available: settings.doc_rules,
				additions: pageAsMedia ? <ReferenceChecks event={pageAsMedia} /> : undefined,
			},
		] as scores.subScoreStruct[],
	} as scores.scoreStruct

	const safety = {
		title: "Brand Safety",
		indev: false,
		subtitle: "Detailed view of the Brand Safety score",
		subscores: [
			{
				title: "Violence Check",
				score: getScore(currentPage?.doc_violence, item.event?.doc_violence),
				inverse: true,
				indev: false,
				available: settings.doc_violence,
				additions: pageAsMedia ? <Violence event={pageAsMedia} /> : undefined,
			},
			{
				title: "Nudity Check",
				score: getScore(currentPage?.doc_nudity, item.event?.doc_nudity),
				inverse: true,
				indev: false,
				available: settings.doc_nudity,
				additions: pageAsMedia ? <Nudity event={pageAsMedia} /> : undefined,
			},
			{
				title: "Racism Check",
				score: getScore(currentPage?.doc_racism, item.event?.doc_racism),
				inverse: true,
				indev: false,
				available: settings.doc_racism,
				additions: pageAsMedia ? <Racism event={pageAsMedia} /> : undefined,
			},
			{
				title: "AI Generated Check",
				...scores.assessed(
					(currentPage ?? item.event)?.doc_ai_generated,
					(currentPage ?? item.event)?.doc_ai_generated_explanation ?? "",
				),
				inverse: true,
				indev: false,
				additions: <Explanation explanation={(currentPage ?? item.event)?.doc_ai_generated_explanation ?? ""} />,
			},
		] as scores.subScoreStruct[],
	} as scores.scoreStruct

	const quality = {
		title: "Content Quality",
		indev: false,
		subtitle: "Detailed view of the Content Quality score",
		subscores: [
			{
				title: "Grammar Check",
				score: getScore(currentPage?.doc_grammar, item.event?.doc_grammar),
				inverse: false,
				indev: false,
				available: settings.doc_grammar,
				additions: pageAsMedia ? <Grammar event={pageAsMedia} /> : undefined,
			},
		] as scores.subScoreStruct[],
	} as scores.scoreStruct

	function NonNegotiableAlert(props: layouts.containers.FlexProps): JSX.Element {
		const { ...rest } = props

		const _onbrand = onBrand.subscores.map((i) => scores.isNonNegotiable(i.score, i.indev, i.inverse, i.available))
		const _onstyle = onStyle.subscores.map((i) => scores.isNonNegotiable(i.score, i.indev, i.inverse, i.available))
		const _safety = safety.subscores.map((i) => scores.isNonNegotiable(i.score, i.indev, i.inverse, i.available))
		const _quality = quality.subscores.map((i) => scores.isNonNegotiable(i.score, i.indev, i.inverse, i.available))
		if (!_onbrand.concat(_onstyle, _safety, _quality).includes(true)) return <></>
		return <scores.NonNegotiableMessage {...rest} />
	}

	//If there is only one category enabled => expand that category by default
	const initOpen = (): boolean => {
		const _onbrand = scores.sum(onBrand.subscores.map((i) => i.available)) === 0 ? 0 : 1
		const _onstyle = scores.sum(onStyle.subscores.map((i) => i.available)) === 0 ? 0 : 1
		const _safety = scores.sum(safety.subscores.map((i) => i.available)) === 0 ? 0 : 1
		const _quality = scores.sum(quality.subscores.map((i) => i.available)) === 0 ? 0 : 1
		return _onbrand + _onstyle + _safety + _quality === 1
	}

	return (
		<bglayouts.fileDetails.InformationGroup
			className={classnames(className, "file-details-information-group")}
			{...rest}
		>
			{settings.confidence !== State.DISABLED && (
				<scores.BGScore
					confidence={item.event!.confidence}
					className={classnames(className, "brandguard-score")}
				/>
			)}
			<scores.UploadedBy profileId={item.media?.uploaded_by ?? ""} />
		<NonNegotiableAlert height="30px" borderRadius="5px" alignItems="center" mb="15px" {...parentRest} />
		<scores.ScoreItem i={onBrand} className={className} initOpen={initOpen()} />
		<scores.ScoreItem i={onStyle} className={className} initOpen={initOpen()} />
		<scores.ScoreItem i={safety} className={className} initOpen={initOpen()} />
		<scores.ScoreItem i={quality} className={className} initOpen={initOpen()} />
		{settings.doc_product_detection !== State.DISABLED && pageAsMedia && (
			<ProductDetection event={pageAsMedia} contentType="document">
				<layouts.Span fontWeight="500">Detected Products</layouts.Span>
			</ProductDetection>
		)}
		</bglayouts.fileDetails.InformationGroup>
	)
}

Scoring.defaultProps = {
	prediction: (i: api.DocumentSearchResponseItem) => i.event?.approved || api.Prediction.NONE,
}
